"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

// Lightweight admin toast: any client code calls notifyToast(ok) and the
// <Toaster> mounted in the admin layout renders a "Done" / "Failed" popup.

let seq = 0;

type Item = { id: number; ok: boolean };

/** Fire a success/failure toast (no-op during SSR). */
export function notifyToast(ok: boolean) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("admin-toast", { detail: { ok } }));
}

/** Wrap a server action so it shows a toast when it resolves or throws. */
export function withToast<A extends unknown[]>(
  action: (...args: A) => unknown | Promise<unknown>,
) {
  return async (...args: A) => {
    try {
      await action(...args);
      notifyToast(true);
    } catch {
      notifyToast(false);
    }
  };
}

export function Toaster() {
  const t = useTranslations("Admin");
  const [items, setItems] = useState<Item[]>([]);

  useEffect(() => {
    function onToast(e: Event) {
      const ok = Boolean((e as CustomEvent).detail?.ok);
      const id = ++seq;
      setItems((prev) => [...prev, { id, ok }]);
      window.setTimeout(
        () => setItems((prev) => prev.filter((x) => x.id !== id)),
        3500,
      );
    }
    window.addEventListener("admin-toast", onToast);
    return () => window.removeEventListener("admin-toast", onToast);
  }, []);

  return (
    <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      {items.map((it) => (
        <div
          key={it.id}
          role="status"
          className={`pointer-events-auto flex items-center gap-2.5 rounded-2xl border px-4 py-3 text-sm font-medium shadow-lg shadow-bark/10 ${
            it.ok
              ? "border-forest-200 bg-forest-50 text-forest-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <span
            className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-xs text-white ${
              it.ok ? "bg-forest-500" : "bg-red-500"
            }`}
          >
            {it.ok ? "✓" : "✕"}
          </span>
          {it.ok ? t("toastDone") : t("toastFailed")}
        </div>
      ))}
    </div>
  );
}
